<template>
    <div class="audit-record">
        <section class="audit-summary">
            <div class="audit-summary-copy">
                <div class="audit-heading">
                    <i class="fi fi-audit title-icon" aria-hidden="true"></i>
                    <div>
                        <span class="section-label">{{
                            $t('patty.auditReferences.types.audit')
                        }}</span>
                        <h3>{{ auditName }}</h3>
                    </div>
                </div>
                <p v-if="siteName" class="audit-site">
                    <i class="fi fi-location" aria-hidden="true"></i>
                    {{ siteName }}
                </p>
            </div>
            <div v-if="displayScore !== null" class="audit-score">
                <span class="section-label">{{
                    $t('audits.panels.summary.overall')
                }}</span>
                <ProgressCircle
                    :percentage="displayScore"
                    :diameter="72"
                    :stroke-width="12"
                    font-size="1rem"
                />
            </div>
        </section>

        <section v-if="hasCounts" class="audit-counts">
            <div v-if="gapCount !== null" class="count-item">
                <i class="fi fi-alert-circle" aria-hidden="true"></i>
                <div>
                    <strong>{{ gapCount }}</strong>
                    <span>{{ $t('patty.auditReferences.fields.gaps') }}</span>
                </div>
            </div>
            <div v-if="actionPointCount !== null" class="count-item">
                <i class="fi fi-clipboard" aria-hidden="true"></i>
                <div>
                    <strong>{{ actionPointCount }}</strong>
                    <span>{{
                        $t('patty.auditReferences.fields.unresolvedActions')
                    }}</span>
                </div>
            </div>
        </section>

        <section v-if="metadata.length" class="record-section">
            <h4>{{ $t('patty.auditReferences.sections.details') }}</h4>
            <dl class="metadata-grid">
                <div
                    v-for="field in metadata"
                    :key="field.label"
                    class="metadata-item"
                >
                    <dt>{{ field.label }}</dt>
                    <dd>{{ field.value }}</dd>
                </div>
            </dl>
        </section>

        <section v-if="auditDetails" class="record-section">
            <h4>{{ $t('patty.auditReferences.fields.notes') }}</h4>
            <p class="audit-details">{{ auditDetails }}</p>
        </section>

        <section v-if="pdfFiles.length" class="record-section">
            <h4>{{ $t('patty.auditReferences.sections.documents') }}</h4>
            <div class="document-list">
                <div
                    v-for="file in pdfFiles"
                    :key="file.hash"
                    class="document-row"
                >
                    <i class="fi fi-file document-icon" aria-hidden="true"></i>
                    <div class="document-copy">
                        <strong>{{ file.title }}</strong>
                        <span v-if="formatFileSize(file.sizeBytes)">{{
                            formatFileSize(file.sizeBytes)
                        }}</span>
                    </div>
                    <div class="document-actions">
                        <button
                            type="button"
                            class="icon-btn icon-btn-primary"
                            :title="$t('patty.auditReferences.actions.viewPdf')"
                            :disabled="loadingFileHash === file.hash"
                            @click="viewPdf(file)"
                        >
                            <i class="fi fi-eye" aria-hidden="true"></i>
                        </button>
                        <button
                            type="button"
                            class="icon-btn icon-btn-light"
                            :title="
                                $t('patty.auditReferences.actions.downloadPdf')
                            "
                            :disabled="loadingFileHash === file.hash"
                            @click="downloadPdf(file)"
                        >
                            <i class="fi fi-download" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>
            <p v-if="fileError" class="document-error" role="alert">
                {{ fileError }}
            </p>
        </section>
    </div>
</template>

<script>
import ProgressCircle from '@/components/ProgressCircle.vue';
import { formatDisplayDate } from '@/utils/dateUtils';
import { formatEnumLabel } from '@/i18n/translationUtils';
import {
    auditReferenceScore,
    createAuditAgentApi,
    resolveAuditStatusLabel,
} from './auditAgent.js';

export default {
    name: 'PattyAuditRecordReadOnly',
    components: { ProgressCircle },
    props: { audit: { type: Object, required: true } },
    data() {
        return { loadingFileHash: null, fileError: '' };
    },
    computed: {
        auditName() {
            return (
                this.audit.name ||
                this.audit.title ||
                this.$t('patty.auditReferences.types.audit')
            );
        },
        siteName() {
            return this.audit.site_name || this.audit.site?.name || null;
        },
        auditDetails() {
            return this.audit.details || this.audit.notes || null;
        },
        pdfFiles() {
            return Array.isArray(this.audit.pdfFiles)
                ? this.audit.pdfFiles.filter(
                      (file) => file?.hash && file?.title
                  )
                : [];
        },
        displayScore() {
            return auditReferenceScore(this.audit);
        },
        gapCount() {
            return this.count(this.audit.gapCount, this.audit.gap_count);
        },
        actionPointCount() {
            return this.count(
                this.audit.unresolvedActionPointCount,
                this.audit.unresolved_action_point_count
            );
        },
        hasCounts() {
            return this.gapCount !== null || this.actionPointCount !== null;
        },
        auditTypeLabel() {
            const type =
                this.audit.audit_type ||
                this.audit.type ||
                this.audit.record_type;
            return type ? formatEnumLabel(type) : null;
        },
        metadata() {
            const date = this.audit.date;
            const completedAt =
                this.audit.completed_at || this.audit.completedAt;
            return [
                [
                    this.$t('patty.auditReferences.fields.auditDate'),
                    date ? formatDisplayDate(date) : null,
                ],
                [
                    this.$t('patty.auditReferences.fields.completedDate'),
                    completedAt ? formatDisplayDate(completedAt) : null,
                ],
                [
                    this.$t('patty.auditReferences.fields.status'),
                    resolveAuditStatusLabel(this.audit, this.$t),
                ],
                [
                    this.$t('patty.auditReferences.fields.type'),
                    this.auditTypeLabel,
                ],
                [
                    this.$t('patty.auditReferences.fields.template'),
                    this.audit.template_name || this.audit.template?.name,
                ],
                [
                    this.$t('patty.auditReferences.fields.conductedBy'),
                    this.audit.auditor_name,
                ],
            ]
                .filter(
                    ([, value]) =>
                        value !== null && value !== undefined && value !== ''
                )
                .map(([label, value]) => ({ label, value: String(value) }));
        },
    },
    methods: {
        count(...values) {
            const value = values.find(
                (candidate) => candidate !== undefined && candidate !== null
            );
            return value === undefined ? null : value;
        },
        formatFileSize(bytes) {
            const size = Number(bytes);
            if (!Number.isFinite(size) || size < 0) return '';
            if (size < 1024) return `${size} B`;
            if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
            return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        },
        async fetchPdf(file, download) {
            this.loadingFileHash = file.hash;
            this.fileError = '';
            try {
                return await createAuditAgentApi({
                    apiRoot: this.$env.apiRoot,
                }).getAuditFile(this.audit.hash, file.hash, { download });
            } catch (error) {
                this.fileError =
                    error?.message ||
                    this.$t('patty.auditReferences.states.fileLoadError');
                return null;
            } finally {
                this.loadingFileHash = null;
            }
        },
        async viewPdf(file) {
            const target = window.open('', '_blank');
            const blob = await this.fetchPdf(file, false);
            if (!blob) {
                target?.close();
                return;
            }
            const url = URL.createObjectURL(blob);
            if (target) target.location.href = url;
            else window.open(url, '_blank');
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        },
        async downloadPdf(file) {
            const blob = await this.fetchPdf(file, true);
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.title || 'audit-report.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        },
    },
};
</script>

<style scoped>
.audit-record {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
}

.audit-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
    padding: var(--space-4);
    border-radius: var(--radius-default);
    background: var(--primary-25);
}

.audit-summary-copy {
    min-width: 0;
}

.audit-heading {
    display: flex;
    align-items: center;
    gap: var(--space-3);
}

.audit-heading h3 {
    margin: 0;
    overflow-wrap: anywhere;
}

.section-label,
.metadata-item dt,
.count-item span {
    color: var(--neutral-700);
    font-size: 0.75rem;
    font-weight: 600;
}

.audit-site {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    margin: var(--space-2) 0 0 3.25rem;
    color: var(--neutral-700);
    font-size: 0.875rem;
}

.audit-score {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    gap: var(--space-1);
}

.audit-counts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-3);
}

.count-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    border-radius: var(--radius-default);
    background: var(--neutral-450);
}

.count-item > i {
    color: var(--primary-500);
    font-size: 1.25rem;
}

.count-item div {
    display: flex;
    flex-direction: column;
}

.count-item strong {
    color: var(--neutral-900);
    font-size: 1.125rem;
}

.record-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
}

.record-section h4 {
    margin: 0;
}

.metadata-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 0;
    border-top: 1px solid var(--neutral-500);
}

.metadata-item {
    min-width: 0;
    padding: var(--space-3) var(--space-3) var(--space-3) 0;
    border-bottom: 1px solid var(--neutral-500);
}

.metadata-item:nth-child(even) {
    padding-right: 0;
    padding-left: var(--space-3);
}

.metadata-item dt {
    margin-bottom: var(--space-1);
}

.metadata-item dd {
    margin: 0;
    color: var(--neutral-900);
    font-weight: 700;
    overflow-wrap: anywhere;
}

.audit-details {
    margin: 0;
    color: var(--neutral-900);
    line-height: 1.5;
    white-space: pre-wrap;
}

.document-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
}

.document-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    border: 1px solid var(--neutral-500);
    border-radius: var(--radius-default);
    background: var(--white);
}

.document-icon {
    color: var(--primary-700);
    font-size: 1.25rem;
}

.document-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
}

.document-copy strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.document-copy span {
    color: var(--neutral-700);
    font-size: 0.75rem;
}

.document-actions {
    display: flex;
    gap: var(--space-2);
}

.document-error {
    margin: 0;
    color: var(--danger);
}

@media (max-width: 575.98px) {
    .audit-summary {
        align-items: flex-start;
    }

    .audit-counts,
    .metadata-grid {
        grid-template-columns: 1fr;
    }

    .metadata-item:nth-child(even) {
        padding-left: 0;
    }
}
</style>
